import { formatMoney, getShortMonthName, toNairobiDate } from "../../utils/format";

// Wording and number rules for the "All Transactions" histories (Rentals,
// Bills & Invoices, Groups). Every label is written from the reader's side
// ("you"), so it is clear who paid whom.

const ACTIVITY_LABELS = {
  mpesa_top_up: "M-PESA top-up",
  money_added: "Money added",
  bill: "Bill to you",
  bill_reduced: "Bill reduced",
  late_fee: "Late fee",
  you_paid: "You paid",
  prepaid: "You prepaid",
  paid_from_credit: "Paid from credit",
  paid_elsewhere: "Paid outside this wallet",
  payment_reversed: "Payment reversed",
  credit_kept: "Credit kept for you",
  debt_moved: "Debt moved",
  rent_received: "You received rent",
  commission: "You received commission",
  payment_received: "You received payment",
  moved_out: "Moved to another wallet",
  moved_in: "Moved from another wallet",
  payout: "Paid out to you",
  payout_failed: "Payout failed",
  withdrawal: "Withdrawal",
  wallet_charge: "Wallet charge",
  adjustment: "Adjustment",
  reversal: "Reversal",
  opening_balance: "Opening balance",
};

const ROLE_LABELS = {
  tenant: "As tenant",
  landlord: "As landlord",
  manager: "As manager",
  customer: "As customer",
  biller: "As biller",
};

export const getActivityLabel = (row) => ACTIVITY_LABELS[row?.kind] || "Other";

// Short stacked date lines ("12 Oct", "2026", "09:51") so the date column stays
// narrow on a phone. A record saved with a date only gets no time line.
export const getDateLines = (value) => {
  const raw = value instanceof Date ? value : new Date(value);
  if (!value || Number.isNaN(raw.getTime())) return [];
  const d = toNairobiDate(raw);
  const lines = [`${String(d.getDate()).padStart(2, "0")} ${getShortMonthName(d.getMonth())}`, String(d.getFullYear())];
  const isDateOnly =
    raw.getUTCHours() === 0 && raw.getUTCMinutes() === 0 && raw.getUTCSeconds() === 0 && raw.getUTCMilliseconds() === 0;
  if (!isDateOnly) {
    lines.push(`${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`);
  }
  return lines;
};

// "As tenant · Sunset · A2", "As customer · Kariuki Water", "Umoja Chama".
// Parts the record does not have are left out.
export const getActivityContext = (row) =>
  [ROLE_LABELS[row?.role], row?.propertyName, row?.unitLabel, row?.companyName, row?.groupName]
    .filter(Boolean)
    .join(" · ");

// An M-PESA top-up already shows its reference; its saved text repeats it.
// Saved text that only repeats the row's label ("Paid from credit") is left out.
export const getActivityDescription = (row) => {
  if (row?.kind === "mpesa_top_up" && row?.reference) return "";
  const description = String(row?.description || "").trim();
  return description.toLowerCase() === getActivityLabel(row).toLowerCase() ? "" : description;
};

// Who sent an M-PESA top-up, as "By: Christopher". Rows without a known name
// (older, recovered or manual deposits) show nothing.
export const getActivityPayer = (row) => {
  const name = String(row?.payerName || "").trim();
  return row?.kind === "mpesa_top_up" && name ? `By: ${name}` : "";
};

// Wallet rows carry a sign: + came into your wallet, - left it. A bill has no
// sign because no money moved.
export const getAmountDisplay = (row) => {
  const amount = Number(row?.amount || 0);
  if (row?.source !== "wallet") return { text: formatMoney(Math.abs(amount)), tone: "bill" };
  if (amount > 0) return { text: `+${formatMoney(amount)}`, tone: "in" };
  if (amount < 0) return { text: `-${formatMoney(Math.abs(amount))}`, tone: "out" };
  return { text: formatMoney(0), tone: "none" };
};

const isNumber = (value) => value !== null && value !== undefined && Number.isFinite(Number(value));

export const getWalletDisplay = (value) => {
  if (!isNumber(value)) return { text: "-", tone: "none" };
  const amount = Number(value);
  return {
    text: amount < 0 ? `-${formatMoney(Math.abs(amount))}` : formatMoney(amount),
    tone: amount < 0 ? "out" : "plain",
  };
};

// What the row's unit still needs from you. A minus means you paid more than
// the bills (credit), as on the unit statement.
export const getOwedDisplay = (value) => {
  if (!isNumber(value)) return { text: "-", tone: "none" };
  const amount = Number(value);
  if (amount > 0) return { text: formatMoney(amount), tone: "owed" };
  if (amount < 0) return { text: `-${formatMoney(Math.abs(amount))}`, tone: "credit" };
  return { text: formatMoney(0), tone: "plain" };
};
